import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, AlertTriangle, CloudLightning, Loader2 } from 'lucide-react';
import { api } from '../api';
import { Card, SectionTitle } from '../ui';
import TripsMap from './TripsMap';
import MapFrame from './MapFrame';

const WM = 'https://www.worldmonitor.app';
const TONE = { red: 'border-[#E5484D] text-[#E37A72]', orange: 'border-[#E8913A] text-[#E8913A]', green: 'border-[#2E9E6A] text-[#5FC18F]' };
const FILTERS = [['all', 'Tutti'], ['security', 'Guerre e tensioni'], ['crisis', 'Crisi umanitarie'], ['health', 'Salute'], ['natural', 'Disastri naturali'], ['expected', 'Attesi']];
const NOT_NATURAL = ['SEC', 'TEN', 'HL', 'CR'];
const match = (e, f) => f === 'all' || (f === 'security' ? ['SEC', 'TEN'].includes(e.type) : f === 'crisis' ? e.type === 'CR' : f === 'health' ? e.type === 'HL'
  : f === 'natural' ? !NOT_NATURAL.includes(e.type) : e.expected);

function WorldMonitorMap() {
  const ref = useRef(null);
  useEffect(() => {
    const s = document.createElement('script');
    Object.assign(s, { src: `${WM}/embed.js`, async: true });
    Object.entries({ panel: 'map', layers: 'conflicts,earthquakes,weather', theme: 'dark', height: '620' }).forEach(([k, v]) => s.setAttribute(`data-${k}`, v));
    ref.current?.appendChild(s);
    const node = ref.current;
    return () => { if (node) node.innerHTML = ''; };
  }, []);
  return <div ref={ref} className="min-h-[620px] min-w-0 overflow-hidden bg-[#050505] [&_iframe]:!w-full [&_iframe]:max-w-full" data-testid="travel-wm-map" />;
}

function EventRow({ e }) {
  const internal = e.url?.startsWith('/');
  const body = (
    <>
      <span className={`mt-1 h-2.5 w-2.5 rounded-full border-2 shrink-0 ${TONE[e.alert] || TONE.green}`} />
      <span className="flex-1 min-w-0">
        <span className="block text-sm text-[#F2F2F2] truncate">{e.type_label} · {e.title}</span>
        <span className="block text-xs text-[#7A7F87] truncate">{[e.detail, e.expected && 'atteso / in evoluzione', e.source].filter(Boolean).join(' · ')}</span>
      </span>
    </>
  );
  const cls = 'flex gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors';
  return internal ? <Link to={e.url} className={cls} data-testid={`travel-event-${e.id}`}>{body}</Link>
    : <a href={e.url} target="_blank" rel="noreferrer" className={cls} data-testid={`travel-event-${e.id}`}>{body}</a>;
}

export default function TravelRiskMap() {
  const [events, setEvents] = useState(null);
  const [f, setF] = useState('all');
  const [view, setView] = useState('trips');
  useEffect(() => { api.get('/travel/events').then((r) => setEvents(r.data)).catch(() => setEvents([])); }, []);
  const shown = useMemo(() => (events || []).filter((e) => match(e, f)), [events, f]);
  const grave = (events || []).filter((e) => e.alert === 'red').length;
  return (
    <Card className="mb-6 overflow-hidden" data-testid="travel-risk-map">
      <div className="px-6 pt-5 pb-3 flex flex-wrap items-center justify-between gap-3">
        <SectionTitle><span className="inline-flex items-center gap-2"><CloudLightning size={15} className="text-[#D4AF37]" />Rischi principali per i viaggi</span></SectionTitle>
        <div className="flex flex-wrap gap-2">
          {[['trips', 'Le mie trasferte'], ['wm', 'Mappa World Monitor']].map(([k, l]) => <button key={k} onClick={() => setView(k)} data-testid={`travel-view-${k}`} className={`h-9 px-3 text-sm rounded-sm border transition-colors ${view === k ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#1F242C] text-[#8A8F97] hover:text-[#F2F2F2]'}`}>{l}</button>)}
        <a href={WM} target="_blank" rel="noreferrer" className="h-9 px-3 flex items-center gap-2 text-sm border border-[#1F242C] rounded-sm text-[#A3A3A3] hover:text-[#D4AF37]" data-testid="travel-wm-open"><ExternalLink size={14} />Apri World Monitor</a>
        </div>
      </div>
      {view === 'trips' ? <div className="border-t border-[#1F242C]"><TripsMap events={events} /></div> : (
      <div className="border-t border-[#1F242C]">
        <MapFrame testid="travel-wm-frame"><WorldMonitorMap /></MapFrame>
        <div className="border-t border-[#1F242C] flex flex-col max-h-[460px] min-w-0">
          <div className="flex flex-wrap gap-1 p-3 border-b border-[#1F242C]">
            {FILTERS.filter(([k]) => k !== 'crisis' || (events || []).some((e) => e.type === 'CR')).map(([k, l]) => <button key={k} onClick={() => setF(k)} data-testid={`travel-events-filter-${k}`} className={`h-8 px-3 text-xs rounded-sm border transition-colors ${f === k ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#1F242C] text-[#8A8F97] hover:text-[#F2F2F2]'}`}>{l}</button>)}
          </div>
          {grave > 0 && <p className="px-4 py-2 text-xs text-[#E37A72] flex items-center gap-2 border-b border-[#1F242C]" data-testid="travel-events-grave"><AlertTriangle size={13} />{grave} eventi con allerta rossa</p>}
          <div className="overflow-y-auto flex-1 divide-y divide-[#1F242C]" data-testid="travel-events-list">
            {!events && <div className="p-4"><Loader2 className="animate-spin text-[#D4AF37]" size={18} /></div>}
            {events && shown.length === 0 && <p className="p-4 text-sm text-[#5A5F67]" data-testid="travel-events-empty">Nessun evento in questa categoria.</p>}
            {shown.slice(0, 80).map((e) => <EventRow key={e.id} e={e} />)}
          </div>
        </div>
      </div>
      )}
      <p className="px-6 py-3 text-[11px] text-[#5A5F67] border-t border-[#1F242C]">Mappa: <a href={WM} target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:underline">World Monitor</a> (conflitti, terremoti, allerte meteo; aggiornamento orario). Elenco: GDACS (ONU · Commissione Europea) per disastri naturali in corso e attesi, Viaggiare Sicuri per guerre e tensioni, OMS (Disease Outbreak News) e CDC (Travel Health Notices) per epidemie e avvisi sanitari, allerte meteo ufficiali dei prossimi 3 giorni (MeteoAlarm per l'Europa, National Weather Service per gli USA) nelle destinazioni delle trasferte. I viaggiatori in trasferta ricevono un'email per eventi, allerte sanitarie e allerte meteo arancioni o rosse nel Paese di destinazione.</p>
    </Card>
  );
}
